// Persons registry — directory list. A Person is the central managed identity
// other records (personnel / principals / client contacts) link to. This page
// lists persons (search on q), and offers a create flow gated on person.write.
//
// Create flow: the shared PersonForm yields a PersonFormPayload (person scalar
// fields + satellite arrays). We POST the person first, then create each
// satellite row sequentially via the per-category satellite hooks (simple
// fire-in-order; the API has no bulk endpoint), and finally navigate to the new
// detail page.

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePersons, useCreatePerson } from '../api/queries/persons';
import {
  useCreatePersonAddress,
  useCreatePersonPhone,
  useCreatePersonEmail,
  useCreatePersonEmergencyContact,
} from '../api/queries/persons';
import { PersonForm, type PersonFormPayload } from '../components/PersonForm';
import { PersonAvatar } from '../components/PersonAvatar';
import { Button, Dialog, FormField, Input, Table } from '../components/ui';
import { ApiError } from '../api/client';
import { can, geoapifyKey } from '../auth/store';
import { toast } from '../components/Toasts';

function fmtDate(epoch: number | null | undefined): string {
  if (epoch == null) return '—';
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US');
}

export function PersonsPage(): JSX.Element {
  const canWrite = can('person.write');

  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const filters = useMemo(() => (q.trim() ? { q: q.trim() } : {}), [q]);
  const { data, isLoading, isError, error } = usePersons(filters);
  const persons = data?.persons ?? [];

  return (
    <div className="page persons-page">
      <header className="page-header">
        <div>
          <h1>People Directory</h1>
          <p className="page-sub">Central managed identities across the platform.</p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Add person
            </Button>
          </div>
        )}
      </header>

      <section className="filter-bar">
        <div className="filter-search">
          <FormField label="Search">
            <Input
              type="search"
              placeholder="Name or preferred name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </FormField>
        </div>
      </section>

      {isLoading && <div className="page-state">Loading people…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load people.'}
        </div>
      )}

      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Preferred name</Table.HeaderCell>
              <Table.HeaderCell>Photo</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {persons.length === 0 && (
              <Table.Empty colSpan={5}>
                No people match the current search.
              </Table.Empty>
            )}
            {persons.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell tone="strong">
                  <Link to={`/persons/${p.id}`} className="table-link">
                    {p.full_name}
                  </Link>
                </Table.Cell>
                <Table.Cell>{p.preferred_name ?? '—'}</Table.Cell>
                <Table.Cell>
                  <PersonAvatar
                    personId={p.id}
                    photoPath={p.photo_path}
                    fullName={p.full_name}
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell>{fmtDate(p.created_at)}</Table.Cell>
                <Table.Cell className="table-actions">
                  <Button variant="ghost" className="btn-sm" asChild>
                    <Link to={`/persons/${p.id}`}>Open</Link>
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <CreatePersonModal open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

// =============================================================================
// Create person modal — wraps the shared PersonForm. Creates the core person
// first, then each satellite row sequentially (no bulk endpoint exists), then
// navigates to the new detail page.
// =============================================================================

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
}

function CreatePersonModal({ open, onClose }: CreateModalProps): JSX.Element {
  const navigate = useNavigate();
  const createPerson = useCreatePerson();
  const createAddress = useCreatePersonAddress();
  const createPhone = useCreatePersonPhone();
  const createEmail = useCreatePersonEmail();
  const createContact = useCreatePersonEmergencyContact();

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (payload: PersonFormPayload): Promise<void> => {
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await createPerson.mutateAsync(payload.person);
      const personId = res.person.id;

      // Satellites: create each row in order. Done sequentially for simplicity;
      // a partial failure surfaces an error and leaves the already-created rows.
      for (const input of payload.addresses) {
        await createAddress.mutateAsync({ personId, input });
      }
      for (const input of payload.phones) {
        await createPhone.mutateAsync({ personId, input });
      }
      for (const input of payload.emails) {
        await createEmail.mutateAsync({ personId, input });
      }
      for (const input of payload.emergency_contacts) {
        await createContact.mutateAsync({ personId, input });
      }

      toast.success('Person created.');
      onClose();
      navigate(`/persons/${personId}`);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to create person.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog title="Add person" open={open} onClose={onClose} busy={submitting} size="wide">
      {formError && (
        <p className="form-error error-text" role="alert">
          {formError}
        </p>
      )}
      <PersonForm onSubmit={onSubmit} submitting={submitting} geoapifyKey={geoapifyKey()} />
    </Dialog>
  );
}
